/**
 * Oath S19 — Tabbed Product Grid web component.
 *
 * Renders all tabs' product cards server-side; this JS toggles which tab/panel
 * is active (data-active class + hidden attribute on panels). Updates the
 * VIEW ALL link href to match the active tab's collection URL on tab click.
 *
 * Per operator clearance C3: render-all + display toggle (no async/lazy-load).
 * Per C4: default active tab = data-default-tab-index attribute (default 0).
 *
 * A11y: arrow-key navigation (left/right between tabs, home/end to first/last);
 * aria-selected + aria-controls + tabindex follows WAI-ARIA Authoring Practices
 * Tabs Pattern (manual activation — user presses Enter/Space to activate).
 *
 * L-code: L-OATH-S19-PRODUCT-GRID-TABBED-001.
 * S48 HARDENING: extends Horizon @theme/component Component; own-DOM via declarative
 * refs (tab[] / panel[] / viewAll) + super lifecycle. Logic byte-identical.
 */

import { Component } from '@theme/component';

class OathProductGridTabbed extends Component {
  constructor() {
    super();
    this.handleTabClick = this.handleTabClick.bind(this);
    this.handleKeyDown = this.handleKeyDown.bind(this);
  }

  connectedCallback() {
    super.connectedCallback();
    this.tabs = this.refs.tab || [];
    this.panels = this.refs.panel || [];
    this.viewAllLink = this.refs.viewAll;
    if (this.tabs.length === 0 || this.panels.length === 0) return;

    this.tabs.forEach((tab) => {
      tab.addEventListener('click', this.handleTabClick);
      tab.addEventListener('keydown', this.handleKeyDown);
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.tabs.forEach((tab) => {
      tab.removeEventListener('click', this.handleTabClick);
      tab.removeEventListener('keydown', this.handleKeyDown);
    });
  }

  handleTabClick(event) {
    const tab = event.currentTarget;
    const tabIndex = Number(tab.dataset.tabIndex);
    if (Number.isNaN(tabIndex)) return;
    this.activateTab(tabIndex, { focus: false });
  }

  handleKeyDown(event) {
    const key = event.key;
    const currentIndex = this.tabs.indexOf(event.currentTarget);
    if (currentIndex === -1) return;

    let nextIndex = -1;
    if (key === 'ArrowRight') nextIndex = (currentIndex + 1) % this.tabs.length;
    else if (key === 'ArrowLeft') nextIndex = (currentIndex - 1 + this.tabs.length) % this.tabs.length;
    else if (key === 'Home') nextIndex = 0;
    else if (key === 'End') nextIndex = this.tabs.length - 1;
    else if (key === 'Enter' || key === ' ') {
      event.preventDefault();
      this.activateTab(currentIndex, { focus: true });
      return;
    }

    if (nextIndex !== -1) {
      event.preventDefault();
      // Per WAI-ARIA manual activation pattern: arrow keys move focus only;
      // user must press Enter/Space to activate the tab content.
      this.tabs[nextIndex].focus();
    }
  }

  activateTab(targetIndex, { focus = false } = {}) {
    if (targetIndex < 0 || targetIndex >= this.tabs.length) return;

    this.tabs.forEach((tab, i) => {
      const isActive = i === targetIndex;
      tab.classList.toggle('oath-product-grid-tabbed__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
      tab.setAttribute('tabindex', isActive ? '0' : '-1');
    });

    this.panels.forEach((panel, i) => {
      const isActive = i === targetIndex;
      if (isActive) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    // Update VIEW ALL href to active tab's collection URL.
    // Hide View All entirely when active tab has no collection configured —
    // otherwise the link silently falls back to /collections/all which misleads
    // shoppers (tab labeled "For Birthday" pointing at all-products).
    if (this.viewAllLink) {
      const activeTab = this.tabs[targetIndex];
      const hasCollection = activeTab.dataset.hasCollection === 'true';
      if (hasCollection) {
        const collectionUrl = activeTab.dataset.collectionUrl;
        if (collectionUrl) {
          this.viewAllLink.setAttribute('href', collectionUrl);
        }
        this.viewAllLink.hidden = false;
      } else {
        this.viewAllLink.hidden = true;
      }
    }

    if (focus) {
      this.tabs[targetIndex].focus();
    }
  }
}

if (!customElements.get('oath-product-grid-tabbed')) {
  customElements.define('oath-product-grid-tabbed', OathProductGridTabbed);
}
